import { useEffect, useState } from "react";
import { Loader2, Snowflake, Fan, Home, Building, Star, Check, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useEdit } from "@/lib/EditContext";
import { ROOM_IMAGES, METRO_STATIONS } from "@/mock";
import { buildWhatsAppBookingUrl } from "@/lib/whatsapp";

const VARIANT_ICON = {
  "non-ac": Fan,
  "ac": Snowflake,
};

const CATEGORY_ICON = {
  "1bhk-compact": Home,
  "1bhk-large": Home,
  "2bhk": Building,
  "3bhk": Star,
};

const CATEGORY_LABELS = {
  "1bhk-compact": "1 BHK · Compact",
  "1bhk-large": "1 BHK · Large",
  "2bhk": "2 BHK",
  "3bhk": "3 BHK",
};

const categoryLabel = (cat) => CATEGORY_LABELS[cat] || cat.toUpperCase();

const STATIC_ROOMS = [
  { id: "r1", key: "single-nonac", name: "Single Room — Non A/C", category: "single", variant: "non-ac", tags: ["1 Bed", "Fan Cooling", "Furnished", "WiFi"], daily_price: 1800, monthly_price: 0, featured: false },
  { id: "r2", key: "single-ac", name: "Single Room — A/C", category: "single", variant: "ac", tags: ["1 Bed", "Air Conditioned", "Furnished", "WiFi"], daily_price: 2000, monthly_price: 0, featured: false },
  { id: "r3", key: "1bhk-compact", name: "1 BHK Compact (A/C)", category: "1bhk-compact", variant: null, tags: ["1 Bedroom", "Hall", "Kitchen", "Cozy", "A/C"], daily_price: 2800, monthly_price: 0, featured: false },
  { id: "r4", key: "1bhk-large", name: "1 BHK Large (A/C)", category: "1bhk-large", variant: null, tags: ["1 Bedroom", "Hall", "Kitchen", "Spacious", "A/C"], daily_price: 3200, monthly_price: 0, featured: false },
  { id: "r5", key: "2bhk", name: "2 BHK Apartment (A/C)", category: "2bhk", variant: null, tags: ["2 Bedrooms", "Hall", "Kitchen", "2 Baths", "A/C"], daily_price: 4000, monthly_price: 0, featured: false },
  { id: "r6", key: "3bhk", name: "3 BHK Premium (A/C)", category: "3bhk", variant: null, tags: ["3 Bedrooms", "Living Room", "Full Kitchen", "2 Baths", "A/C"], daily_price: 5500, monthly_price: 0, featured: true },
];

const scrollToContact = () => {
  const el = document.getElementById("contact");
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const Rooms = () => {
  const [rooms] = useState(STATIC_ROOMS);
  const loading = false;

  const updateRoom = (id, data) => {
    setRooms((prev) => prev.map((r) => (r.id === id ? { ...r, ...data } : r)));
  };

  const single = rooms.filter((r) => r.category === "single");
  const others = rooms.filter((r) => r.category !== "single");

  return (
    <section
      id="rooms"
      className="py-20 md:py-28 px-5 md:px-10 bg-[#F9F7F3]"
      data-testid="rooms-section"
    >
      <div className="max-w-6xl mx-auto">
        <SectionHeader
          eyebrow="Accommodations"
          title="Our Room"
          titleItalic="Types"
          desc="Fully furnished rooms on daily & monthly basis. Book directly via WhatsApp for the best rates."
        />

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#C8A97E]" />
          </div>
        ) : (
          <>
            {/* Single Room Combined Card */}
            {single.length === 2 && (
              <div
                className="bg-white border border-[#E5E0D8] rounded-[20px] overflow-hidden mb-6 shadow-[0_6px_24px_rgba(0,0,0,0.04)]"
                data-testid="single-room-dual"
              >
                <div
                  className="h-[160px] relative bg-cover bg-center"
                  style={{ backgroundImage: `url(${ROOM_IMAGES["single-ac"]})` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <span className="absolute top-4 left-4 bg-[#C8A97E] text-[#1A1A1A] text-[10px] font-bold tracking-[0.15em] uppercase px-3 py-1 rounded-full">
                    Single Room
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-[22px] font-semibold text-[#1A1A1A] mb-5">
                    Single Room — A/C &amp; Non A/C
                  </h3>
                  <div className="grid md:grid-cols-2 gap-5">
                    {single.map((r) => (
                      <SingleVariant key={r.id} room={r} onUpdate={updateRoom} />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Other rooms */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {others.map((r, i) => {
                if (r.category === "3bhk") {
                  return (
                    <div key={r.id} className="md:col-span-2 lg:col-span-3">
                      <RoomCard room={r} featured onUpdate={updateRoom} />
                    </div>
                  );
                }
                return <RoomCard key={r.id} room={r} onUpdate={updateRoom} />;
              })}
            </div>
          </>
        )}

        {/* Metro cards */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="h-[1px] w-8 bg-[#C8A97E]" />
            <span className="text-[10px] tracking-[0.22em] uppercase text-[#C8A97E] font-semibold">
              Nearest Namma Metro Stations (Green Line)
            </span>
            <span className="h-[1px] w-8 bg-[#C8A97E]" />
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {METRO_STATIONS.map((m) => (
              <div
                key={m.name}
                className="bg-[#1A1A1A] border border-[#C8A97E]/20 rounded-[16px] px-7 py-5 text-center min-w-[220px]"
                data-testid={`metro-${m.name.toLowerCase()}`}
              >
                <div className="inline-flex items-center gap-1.5 bg-[#1a7a42] text-white text-[9px] font-bold tracking-[0.2em] uppercase px-3 py-1 rounded-full mb-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-white" /> {m.line}
                </div>
                <div className="font-serif text-[19px] font-semibold text-white mb-1">{m.name}</div>
                <div className="text-[12px] text-[#C8A97E]">{m.distance}</div>
                {m.note && <div className="text-[10px] text-white/50 mt-1">{m.note}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const SectionHeader = ({ eyebrow, title, titleItalic, desc }) => (
  <div className="text-center mb-14">
    <div className="inline-flex items-center gap-3 mb-3">
      <span className="h-[1px] w-6 bg-[#C8A97E]" />
      <span className="text-[10px] tracking-[0.28em] uppercase text-[#C8A97E] font-semibold">
        {eyebrow}
      </span>
      <span className="h-[1px] w-6 bg-[#C8A97E]" />
    </div>
    <h2 className="font-serif text-[40px] sm:text-[46px] font-semibold text-[#1A1A1A] leading-[1.1] mb-3">
      {title} <em className="text-[#C8A97E] font-normal">{titleItalic}</em>
    </h2>
    <p className="text-[15px] text-[#666] max-w-xl mx-auto leading-relaxed">{desc}</p>
  </div>
);

const SingleVariant = ({ room, onUpdate }) => {
  const Icon = VARIANT_ICON[room.variant] || Fan;
  const isAc = room.variant === "ac";
  return (
    <div className="bg-[#F9F7F3] border border-[#E5E0D8] rounded-[14px] p-5 hover:border-[#C8A97E]/50 transition-colors">
      <div className="flex items-center gap-3 mb-4">
        <div
          className={`w-11 h-11 rounded-full flex items-center justify-center ${
            isAc ? "bg-[#1A1A1A] text-[#C8A97E]" : "bg-[#FAF3E7] text-[#C8A97E]"
          }`}
        >
          <Icon className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div className="flex-1">
          <div className="font-serif text-[17px] font-semibold text-[#1A1A1A] leading-tight">
            {isAc ? "A/C Room" : "Non A/C"}
          </div>
          <div className="text-[11px] text-[#9A9A9A]">
            {isAc ? "Air Conditioned · Furnished" : "Fan · Fully Furnished"}
          </div>
        </div>
        <span
          className={`text-[9px] font-bold tracking-[0.15em] px-2.5 py-1 rounded-full ${
            isAc
              ? "bg-[#1A1A1A] text-[#C8A97E] border border-[#C8A97E]/30"
              : "bg-[#FAF3E7] text-[#9A7B4F] border border-[#E5E0D8]"
          }`}
        >
          {isAc ? "A/C" : "NON A/C"}
        </span>
      </div>
      <Tags tags={room.tags} />
      <div className="mb-3">
        <PriceBox room={room} field="daily_price" unit="/night" label="Daily Rate" onUpdate={onUpdate} />
      </div>
      <div className="text-[11px] text-[#9A9A9A] bg-[#FAF3E7] border border-[#E5E0D8] rounded-[8px] px-3 py-2 mb-3 leading-snug">
        For monthly stays, please use the <button onClick={scrollToContact} className="text-[#C8A97E] font-semibold underline-offset-2 hover:underline">enquiry form</button>.
      </div>
      <div className="flex gap-2">
        <Button
          onClick={scrollToContact}
          className="flex-1 bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white rounded-[10px] h-10 text-[12px] font-semibold tracking-wide"
          data-testid={`enquire-${room.key}`}
        >
          Enquire
        </Button>
        <a
          href={buildWhatsAppBookingUrl({
            roomName: `Single Room ${room.variant === "ac" ? "(A/C)" : "(Non A/C)"}`,
            basis: "Daily",
          })}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-1.5 bg-[#25D366] hover:bg-[#20b858] text-white rounded-[10px] h-10 px-3 text-[12px] font-semibold tracking-wide shadow-[0_4px_12px_rgba(37,211,102,0.3)] transition-all"
          data-testid={`whatsapp-book-${room.key}`}
          title="Book on WhatsApp"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          Book
        </a>
      </div>
    </div>
  );
};

const RoomCard = ({ room, featured, onUpdate }) => {
  const Icon = CATEGORY_ICON[room.category] || Home;
  const img = ROOM_IMAGES[room.key];
  return (
    <div
      className={`bg-white border rounded-[20px] overflow-hidden transition-all shadow-[0_6px_24px_rgba(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_14px_36px_rgba(0,0,0,0.08)] ${
        featured ? "border-[#C8A97E]/40 shadow-[0_8px_30px_rgba(200,169,126,0.15)]" : "border-[#E5E0D8]"
      }`}
      data-testid={`room-card-${room.key}`}
    >
      <div
        className={`relative ${featured ? "h-[220px]" : "h-[170px]"} bg-cover bg-center`}
        style={{ backgroundImage: `url(${img})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
        {featured ? (
          <span className="absolute top-4 left-4 bg-gradient-to-r from-[#C8A97E] to-[#E8D9BF] text-[#1A1A1A] text-[10px] font-bold tracking-[0.15em] uppercase px-3 py-1 rounded-full inline-flex items-center gap-1">
            <Star className="w-3 h-3 fill-current" /> Premium 3 BHK
          </span>
        ) : (
          <span className="absolute top-4 left-4 bg-[#1A1A1A] text-[#C8A97E] text-[10px] font-bold tracking-[0.15em] uppercase px-3 py-1 rounded-full border border-[#C8A97E]/30">
            {categoryLabel(room.category)}
          </span>
        )}
        <div className="absolute bottom-3 right-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#1A1A1A]" strokeWidth={1.5} />
        </div>
      </div>
      <div className="p-6">
        <h3 className="font-serif text-[20px] font-semibold text-[#1A1A1A] mb-2.5">{room.name}</h3>
        <Tags tags={room.tags} />
        <div className="border-t border-[#E5E0D8] pt-4">
          <div className="mb-3">
            <PriceBox room={room} field="daily_price" unit="/night" label="Daily Rate" onUpdate={onUpdate} />
          </div>
          <div className="text-[11px] text-[#9A9A9A] bg-[#FAF3E7] border border-[#E5E0D8] rounded-[8px] px-3 py-2 mb-4 leading-snug">
            For monthly stays, please use the <button onClick={scrollToContact} className="text-[#C8A97E] font-semibold underline-offset-2 hover:underline">enquiry form</button>.
          </div>
          <div className="flex gap-2">
            <Button
              onClick={scrollToContact}
              className="flex-1 bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white rounded-[10px] h-11 text-[13px] font-semibold tracking-wide"
              data-testid={`enquire-${room.key}`}
            >
              Enquire
            </Button>
            <a
              href={buildWhatsAppBookingUrl({ roomName: room.name })}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1.5 bg-[#25D366] hover:bg-[#20b858] text-white rounded-[10px] h-11 px-4 text-[13px] font-semibold tracking-wide shadow-[0_4px_14px_rgba(37,211,102,0.32)] transition-all"
              data-testid={`whatsapp-book-${room.key}`}
              title="Book on WhatsApp"
            >
              <MessageCircle className="w-4 h-4" />
              Book on WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const Tags = ({ tags }) => (
  <div className="flex flex-wrap gap-1.5 mb-4">
    {tags.map((t) => (
      <span
        key={t}
        className="text-[10px] text-[#666] bg-[#FAF3E7] border border-[#E5E0D8] px-2.5 py-1 rounded-full font-medium"
      >
        {t}
      </span>
    ))}
  </div>
);

const PriceBox = ({ room, field, unit, label, onUpdate }) => {
  const { editMode } = useEdit();
  const [value, setValue] = useState(room[field] || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(room[field] || "");
  }, [room, field]);

  const save = async () => {
    const num = parseInt(value, 10);
    if (isNaN(num) || num === room[field]) return;
    setSaving(true);
    try {
      await api.updateRoom(room.id, { [field]: num });
      onUpdate(room.id, { [field]: num });
      toast.success("Price updated");
    } catch {
      toast.error("Could not update — please sign in again");
      setValue(room[field] || "");
    } finally {
      setSaving(false);
    }
  };

  const display = room[field] ? `₹${Number(room[field]).toLocaleString("en-IN")}` : "—";

  return (
    <div>
      <div className="text-[9px] tracking-[0.18em] uppercase text-[#9A9A9A] font-semibold mb-1.5">
        {label}
      </div>
      <div
        className={`flex items-center gap-1.5 rounded-[10px] px-3 py-2 transition-all ${
          editMode
            ? "bg-[#FAF3E7] border-2 border-dashed border-[#C8A97E] focus-within:border-solid focus-within:bg-[#FDF8EF]"
            : "bg-[#F9F7F3] border border-[#E5E0D8]"
        }`}
      >
        <span className="font-serif text-[14px] font-bold text-[#C8A97E]">₹</span>
        {editMode ? (
          <input
            type="number"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={save}
            placeholder="Rate"
            className="price-input flex-1 bg-transparent border-0 outline-none text-[15px] font-semibold text-[#1A1A1A] placeholder:text-[#9A9A9A] placeholder:font-normal min-w-0"
            data-testid={`price-${room.key}-${field}`}
          />
        ) : (
          <span className="flex-1 text-[15px] font-semibold text-[#1A1A1A] truncate">
            {display}
          </span>
        )}
        <span className="text-[10px] text-[#9A9A9A] font-medium whitespace-nowrap">{unit}</span>
        {saving && <Loader2 className="w-3 h-3 animate-spin text-[#C8A97E]" />}
      </div>
    </div>
  );
};

export default Rooms;
